import type { GradeResult } from '../lib/grade'
import type { InputMode } from '../types'
import { scoreClass } from '../lib/analysis'
import { languageOf } from '../lib/languages'
import { ToneScore } from './ToneRibbon'
import { SpeakButton } from './SpeakButton'

const STATUS_NOTE: Record<string, string> = {
  correct: 'right',
  tone: 'tone',
  wrong: 'wrong',
  missing: 'dropped',
  extra: 'extra',
}

/**
 * The point of the report is not the number — it is showing the learner the
 * exact unit they dropped. The number is there so the scheduler has something
 * to act on.
 */
export function GradeReport({
  result,
  mode,
  target,
  language = 'zh',
  showAudio = true,
}: {
  result: GradeResult
  mode: InputMode
  target: { hanzi: string; pinyin: string }
  language?: string
  /**
   * Off only where the same pair already sits a few pixels above — two
   * identical Play/Slower pairs on one screen read as a bug, not a choice.
   */
  showAudio?: boolean
}) {
  const { counts } = result
  const lang = languageOf(language)

  // Mandarin typed as characters has no near-miss tier: a character is right or
  // it is not. Every other combination can produce one.
  const showsNearMiss = mode !== 'hanzi'

  return (
    <div className="card appear">
      <div className="row" style={{ alignItems: 'flex-end', gap: '1.5rem' }}>
        <div>
          <div className={`score ${scoreClass(result.score)}`}>{result.score}</div>
          <div className="stat-label">score</div>
        </div>

        {showsNearMiss && result.toneAccuracy !== null && (
          <div>
            <div className="stat-value">{result.toneAccuracy}%</div>
            <div className="stat-label">{lang.nearMissLabel}s right</div>
          </div>
        )}

        <div className="tally" style={{ marginLeft: 'auto' }}>
          <span>
            <span className="tally-n">{counts.correct}</span> right
          </span>
          {showsNearMiss && (
            <span>
              <span className="tally-n">{counts.tone}</span> {lang.nearMissLabel}
            </span>
          )}
          <span>
            <span className="tally-n">{counts.wrong}</span> wrong
          </span>
          <span>
            <span className="tally-n">{counts.missing}</span> dropped
          </span>
          <span>
            <span className="tally-n">{counts.extra}</span> extra
          </span>
        </div>
      </div>

      <div className="divider" />

      <div className={`tokens ${mode === 'pinyin' ? 'is-pinyin' : ''}`}>
        {result.tokens.map((tok, i) => (
          <span
            key={i}
            className={`tok is-${tok.status}`}
            title={`${STATUS_NOTE[tok.status]}${
              tok.status === 'wrong' || tok.status === 'tone' ? ` — you wrote ${tok.got}` : ''
            }`}
          >
            {tok.status === 'extra' ? tok.got : tok.expected}
            {(tok.status === 'wrong' || tok.status === 'tone') && (
              <span className="tok-sub">{tok.got}</span>
            )}
          </span>
        ))}
      </div>

      {mode === 'pinyin' && (
        <>
          <div className="divider" />
          <div className="stat-label" style={{ marginBottom: '0.2rem' }}>
            tone contour
          </div>
          <ToneScore tokens={result.tokens} />
        </>
      )}

      <div className="divider" />
      <div
        className="row"
        style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}
      >
        <span className="stat-label">target</span>
        {showAudio && target.hanzi.trim() && (
          <SpeakButton text={target.hanzi} language={lang.code} withSlow />
        )}
      </div>
      <div className={lang.code === 'zh' ? 'hanzi' : ''} style={{ fontSize: '1.1rem' }}>
        {target.hanzi}
      </div>
      {lang.hasPhonetics && <div className="pinyin">{target.pinyin}</div>}
    </div>
  )
}
